import React, { useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import SuccessAlert from '../../../components/UI/Alert/SuccessAlert';
import ChangeYourPassword from '../../../components/Profile/ChangeYourPassword';
import DeactivateYourAccount from '../../../components/Profile/DeactivateYourAccount';
import { useUserContext } from '../../../contexts/UserContext';
import { sendResetPasswordEmail, updateUserProfile } from '../../../utils/APIs';
import ErrorAlert from '../../../components/UI/Alert/ErrorAlert';
import Loader from "../../../components/UI/Loader/Loader";
import { permission } from '../../../config/permission';
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import { ANALYTICS_EVENTS } from '../../../constants/analyticsEvents';
import { Card, Button, Input } from '../../../components/UI';
import { User, Mail, Shield, KeyRound, AlertTriangle } from 'lucide-react';

const Profile = () => {
    const { user, getAccessTokenSilently } = useAuth0();
    const { userData, setUserData, checkPermission, isOwnerOfSelectedOrganization } = useUserContext();
    const { content } = useContentfulContext();

    const [changePassword, setChangePassword] = useState(false);
    const [deactivateAccount, setDeactivateAccount] = useState(false);
    const [userName, setUserName] = useState(userData?.name || "");
    const [userValid, setUserValid] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [passwordTimer, setPasswordTimer] = useState(0);

    useEffect(() => {
        if (userName === userData?.name || userName.length < 3) {
            setUserValid(false);
        } else {
            setShowSuccess(false);
            setUserValid(true);
        }
    }, [userName, userData?.name]);

    useEffect(() => {
        let interval;
        if (passwordTimer > 0) {
            interval = setInterval(() => {
                setPasswordTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [passwordTimer]);

    const handleSaveChanges = async () => {
        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const result = await updateUserProfile(token, user.sub, { name: userName }, userData?.selectedOrganization?.org_token);
            const updatedName = result?.data?.name || result?.data?.user_metadata?.name || userName;
            setUserName(updatedName);
            setUserData((prev) => ({ ...prev, name: updatedName }));
            setLoading(false);
            setShowSuccess(true);
        } catch (error) {
            setLoading(false);
            setErrorMessage(error?.response?.data?.message || "Failed to update profile");
        }
    };

    if (changePassword) {
        return <ChangeYourPassword setChangePassword={setChangePassword} />;
    }

    const handleResetPassword = async () => {
        if (passwordTimer > 0) return;
        setLoading(true);
        try {
            const result = await sendResetPasswordEmail(userData.email, userData.selectedOrganization.org_token, {
                event_name: ANALYTICS_EVENTS.RESET_PASSWORD,
                metadata: { email: userData.email }
            });
            setLoading(false);
            setSuccessMessage(result?.message);
            setPasswordTimer(60);
        } catch (error) {
            setLoading(false);
            setErrorMessage(error?.response?.data?.message || error?.message);
        }
    };

    return (
        <>
            {loading && <Loader />}
            <main id="main" className="stride-page-shell flex flex-col space-y-6">
                <div>
                    <span className="stride-eyebrow">Settings / Account</span>
                    <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                        {content.PROFILE_CONTENT.title}
                    </h1>
                    <p className="text-sm text-stride-muted mt-1">
                        Manage your personal profile details, credentials, and account settings.
                    </p>
                </div>

                {showSuccess && (
                    <SuccessAlert disappearTime={content.PROFILE_CONTENT.timeout}>
                        {content.PROFILE_CONTENT.errors.FE117}
                    </SuccessAlert>
                )}
                {successMessage && (
                    <SuccessAlert disappearTime={content.PROFILE_CONTENT.change_password.timeout}>
                        {successMessage}
                    </SuccessAlert>
                )}
                {errorMessage && (
                    <ErrorAlert timeout={content.PROFILE_CONTENT.change_password.timeout}>
                        {errorMessage}
                    </ErrorAlert>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start max-w-5xl">
                    {/* Profile Information Card */}
                    <Card variant="default" padding="lg" className="border border-border shadow-card flex flex-col space-y-5">
                        <div className="flex items-center gap-3 pb-3 border-b border-border">
                            <div className="w-9 h-9 rounded-xl bg-primary-soft text-primary flex items-center justify-center">
                                <User className="w-5 h-5" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-stride-heading">Personal Details</h2>
                                <p className="text-xs text-stride-muted">Your public identity and assigned organization role</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-stride-muted uppercase tracking-wider mb-1.5">
                                    {content.PROFILE_CONTENT.manage_profile.email_label}
                                </label>
                                <Input
                                    id="email"
                                    type="text"
                                    value={userData?.email || ""}
                                    disabled
                                    prefixIcon={<Mail className="w-4 h-4 text-stride-muted" />}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-stride-muted uppercase tracking-wider mb-1.5">
                                    {content.PROFILE_CONTENT.manage_profile.organization_role_label}
                                </label>
                                <Input
                                    type="text"
                                    value={userData?.selectedOrganization?.role?.[0]?.name || "Member"}
                                    disabled
                                    prefixIcon={<Shield className="w-4 h-4 text-stride-muted" />}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-stride-muted uppercase tracking-wider mb-1.5">
                                    {content.PROFILE_CONTENT.manage_profile.name_label}
                                </label>
                                <Input
                                    id="name"
                                    type="text"
                                    value={userName}
                                    onChange={(e) => setUserName(e.target.value)}
                                    placeholder="Enter your full name"
                                    prefixIcon={<User className="w-4 h-4 text-stride-muted" />}
                                />
                            </div>

                            <div className="pt-2">
                                <Button
                                    variant="primary"
                                    onClick={handleSaveChanges}
                                    disabled={!userValid}
                                >
                                    {content.PROFILE_CONTENT.manage_profile.cta_text}
                                </Button>
                            </div>
                        </div>
                    </Card>

                    {/* Security & Password Card */}
                    <div className="flex flex-col space-y-6">
                        <Card variant="default" padding="lg" className="border border-border shadow-card flex flex-col space-y-5">
                            <div className="flex items-center gap-3 pb-3 border-b border-border">
                                <div className="w-9 h-9 rounded-xl bg-primary-soft text-primary flex items-center justify-center">
                                    <KeyRound className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-base font-bold text-stride-heading">
                                        {content.PROFILE_CONTENT.change_password.title}
                                    </h2>
                                    <p className="text-xs text-stride-muted">Request a secure password reset link to your email</p>
                                </div>
                            </div>

                            <p className="text-sm text-stride-body">
                                {content.PROFILE_CONTENT.change_password.description}
                            </p>

                            <div>
                                <Button
                                    id="send-reset-password-email-btn"
                                    variant="secondary"
                                    onClick={handleResetPassword}
                                    disabled={passwordTimer > 0}
                                >
                                    {passwordTimer > 0
                                        ? `${content.PROFILE_CONTENT.change_password.cta_text} (${passwordTimer}s)`
                                        : content.PROFILE_CONTENT.change_password.cta_text}
                                </Button>
                            </div>
                        </Card>

                        {/* Danger Zone: Deactivate Account */}
                        {isOwnerOfSelectedOrganization() && checkPermission(permission.MANAGE_PROFILE_OP_DEACTIVATE_ACCOUNT) && (
                            <Card variant="default" padding="lg" className="border border-rose-200 bg-rose-50/20 shadow-xs flex flex-col space-y-4">
                                <div className="flex items-center gap-3 pb-2 border-b border-rose-100">
                                    <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
                                        <AlertTriangle className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-bold text-rose-900">
                                            {content.PROFILE_CONTENT.deactivate_account.title}
                                        </h2>
                                        <p className="text-xs text-rose-700">Permanent account removal</p>
                                    </div>
                                </div>

                                <p className="text-sm text-stride-body">
                                    {content.PROFILE_CONTENT.deactivate_account.description}
                                </p>

                                <div>
                                    <Button
                                        variant="danger"
                                        onClick={() => setDeactivateAccount(true)}
                                    >
                                        {content.PROFILE_CONTENT.deactivate_account.cta_text}
                                    </Button>
                                </div>
                            </Card>
                        )}
                    </div>
                </div>
            </main>
            {deactivateAccount && <DeactivateYourAccount setDeactivateAccount={setDeactivateAccount} />}
        </>
    );
};

export default Profile;
